/**
 * Elvorn — Fiche produit premium (JavaScript natif, aucune dépendance).
 * Chaque fonction init* est idempotente et relancée quand l'éditeur de thème recharge une section.
 */

let ThemeCartEvent = null;
import('@shopify/events')
  .then((m) => {
    ThemeCartEvent = m.CartLinesUpdateEvent || null;
  })
  .catch(() => {});

const once = (el, key) => {
  if (!el || el.dataset[key]) return false;
  el.dataset[key] = '1';
  return true;
};

/* ------------------------------------------------------------------ */
/* Fiche produit : variantes, galerie, visionneuse, panier, barre mobile */
/* ------------------------------------------------------------------ */
function initProduct(root) {
  if (!once(root, 'elvReady')) return;

  const variants = JSON.parse(root.querySelector('[data-elv-variants]')?.textContent || '[]');
  const form = root.querySelector('form[action*="/cart/add"]');
  const idInput = root.querySelector('[data-elv-variant-input]');
  const fieldsets = [...root.querySelectorAll('.elv-option')];
  const atc = root.querySelector('[data-elv-atc]');
  const atcLabel = root.querySelector('[data-elv-atc-label]');
  const msg = root.querySelector('[data-elv-atc-msg]');
  const sticky = root.querySelector('[data-elv-sticky]');
  const stickyBtn = root.querySelector('[data-elv-sticky-btn]');
  const track = root.querySelector('[data-elv-gal-track]');

  const $ = (sel) => root.querySelector(sel);

  /* ---------- Variantes ---------- */
  const selectedOptions = () =>
    fieldsets.map((fs) => fs.querySelector('input:checked')?.value ?? null);

  const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

  const refreshAvailability = () => {
    const current = selectedOptions();
    fieldsets.forEach((fs, index) => {
      fs.querySelectorAll('input').forEach((input) => {
        const probe = [...current];
        probe[index] = input.value;
        const match = findVariant(probe);
        const available = Boolean(match && match.available);
        input.toggleAttribute('data-unavailable', !available);
        // Une seule option : une valeur épuisée ne peut pas être choisie.
        if (fieldsets.length === 1) input.disabled = !match || !match.available;
      });
    });
  };

  const setText = (el, text) => {
    if (el) el.textContent = text;
  };

  const applyVariant = (variant) => {
    const available = Boolean(variant && variant.available);

    if (variant) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));

      setText($('[data-elv-price-now]'), variant.price);
      setText($('[data-elv-sticky-price]'), variant.price);
      setText($('[data-elv-sticky-variant]'), variant.title);

      const was = $('[data-elv-price-was]');
      const badge = $('[data-elv-price-badge]');
      if (was && badge) {
        was.hidden = !variant.compare;
        badge.hidden = !variant.compare;
        if (variant.compare) {
          was.innerHTML = `<span class="visually-hidden">Prix initial :</span>${variant.compare}`;
          badge.textContent = `−${variant.saving} %`;
        }
      }

      const skuWrap = $('[data-elv-sku-wrap]');
      if (skuWrap) {
        skuWrap.hidden = !variant.sku;
        setText($('[data-elv-sku]'), variant.sku || '');
      }

      const dot = $('[data-elv-stock-dot]');
      if (dot) {
        dot.dataset.state = variant.stock;
        setText(
          $('[data-elv-stock-text]'),
          variant.stock === 'in' ? 'En stock' : variant.stock === 'ok' ? 'Disponible à la commande' : 'Actuellement épuisé'
        );
      }

      if (variant.media) showMedia(variant.media);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    fieldsets.forEach((fs) => {
      setText(fs.querySelector('[data-elv-option-value]'), fs.querySelector('input:checked')?.value || '');
    });

    const label = !variant ? 'Indisponible' : available ? 'Ajouter au panier' : 'Épuisé';
    if (atc) atc.disabled = !available;
    setText(atcLabel, label);
    if (stickyBtn) {
      stickyBtn.disabled = !available;
      stickyBtn.textContent = available ? 'Ajouter' : label;
    }
    refreshAvailability();
  };

  fieldsets.forEach((fs) =>
    fs.addEventListener('change', () => applyVariant(findVariant(selectedOptions())))
  );
  if (fieldsets.length) refreshAvailability();

  /* ---------- Galerie ---------- */
  const items = track ? [...track.children] : [];
  const counter = $('[data-elv-gal-current]');

  if (track && counter && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            counter.textContent = Number(entry.target.dataset.index) + 1;
          }
        });
      },
      { root: track, threshold: [0.6] }
    );
    items.forEach((item) => io.observe(item));
  }

  function showMedia(mediaId) {
    const item = items.find((el) => el.dataset.mediaId === String(mediaId));
    if (!item || !track) return;
    const isSlider = getComputedStyle(track).overflowX === 'auto';
    if (isSlider) {
      track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    } else if (item !== track.firstElementChild) {
      // Ordinateur : la photo de la variante passe en première position.
      track.prepend(item);
    }
  }

  /* ---------- Visionneuse ---------- */
  const lb = $('[data-elv-lightbox]');
  if (lb && track) {
    const lbTrack = lb.querySelector('[data-elv-lb-track]');
    const lbSlides = [...lbTrack.children];
    const lbCurrent = lb.querySelector('[data-elv-lb-current]');
    let lastTrigger = null;

    const loadSlide = (i) => {
      [i - 1, i, i + 1].forEach((n) => {
        const img = lbSlides[n]?.querySelector('img[data-src]');
        if (img) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
      });
    };

    const goTo = (i, smooth = true) => {
      const index = Math.max(0, Math.min(lbSlides.length - 1, i));
      loadSlide(index);
      lbTrack.scrollTo({ left: index * lbTrack.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    };

    const currentIndex = () => Math.round(lbTrack.scrollLeft / Math.max(1, lbTrack.clientWidth));

    lbTrack.addEventListener(
      'scroll',
      () => {
        const i = currentIndex();
        if (lbCurrent) lbCurrent.textContent = i + 1;
        loadSlide(i);
        lbTrack.querySelectorAll('img[data-zoomed]').forEach((img) => img.removeAttribute('data-zoomed'));
      },
      { passive: true }
    );

    track.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-elv-zoom]');
      if (!trigger) return;
      lastTrigger = trigger;
      const index = Number(trigger.closest('.elv-gal__item').dataset.index);
      lb.showModal();
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => goTo(index, false));
    });

    lb.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      lastTrigger?.focus();
    });

    lb.querySelector('[data-elv-lb-close]')?.addEventListener('click', () => lb.close());
    lb.querySelector('[data-elv-lb-prev]')?.addEventListener('click', () => goTo(currentIndex() - 1));
    lb.querySelector('[data-elv-lb-next]')?.addEventListener('click', () => goTo(currentIndex() + 1));

    lb.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') goTo(currentIndex() - 1);
      if (event.key === 'ArrowRight') goTo(currentIndex() + 1);
    });

    lbTrack.addEventListener('click', (event) => {
      const img = event.target.closest('.elv-lb__img');
      if (!img) return;
      if (img.hasAttribute('data-zoomed')) {
        img.removeAttribute('data-zoomed');
      } else {
        const rect = img.getBoundingClientRect();
        img.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
        img.setAttribute('data-zoomed', '');
      }
    });
  }

  /* ---------- Ajout au panier ---------- */
  if (form) {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!atc || atc.disabled) return;

      atc.disabled = true;
      if (stickyBtn) stickyBtn.disabled = true;
      msg.removeAttribute('data-error');
      msg.textContent = '';

      const formData = new FormData(form);
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      let deferred = null;
      if (ThemeCartEvent?.createPromise) {
        deferred = ThemeCartEvent.createPromise();
        form.dispatchEvent(
          new ThemeCartEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: String(formData.get('id')), quantity: 1 }],
            promise: deferred.promise,
          })
        );
      }

      const addUrl = (window.Theme?.routes?.cart_add_url || '/cart/add') + '.js';
      const cartUrl = window.Theme?.routes?.cart_url || '/cart';

      try {
        const response = await fetch(addUrl.replace('.js.js', '.js'), {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          msg.setAttribute('data-error', '');
          msg.textContent = data.description || data.message || "Impossible d'ajouter ce produit au panier.";
          deferred?.reject?.(new Error('add failed'));
          return;
        }

        const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        deferred?.resolve?.({
          cart: ThemeCartEvent?.createCartFromAjaxResponse ? ThemeCartEvent.createCartFromAjaxResponse(cart) : cart,
          detail: {
            items: cart.items,
            source: 'elvorn-product',
            sourceId: root.dataset.sectionId,
            itemCount: 1,
            productId: root.dataset.productId,
            sections: data.sections,
            didError: false,
          },
        });

        const cartItems = document.querySelector('cart-items-component');
        if (cartItems && typeof cartItems.fetchCartData === 'function') {
          cartItems.fetchCartData().catch(() => {});
        }

        const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          msg.textContent = 'Ajouté au panier.';
        } else {
          msg.innerHTML = `Ajouté au panier. <a href="${cartUrl}">Voir le panier</a>`;
        }
      } catch (error) {
        msg.setAttribute('data-error', '');
        msg.textContent = 'Une erreur est survenue. Merci de réessayer.';
        deferred?.reject?.(error);
      } finally {
        const variant = variants.find((v) => String(v.id) === String(idInput.value));
        const available = Boolean(variant && variant.available);
        atc.disabled = !available;
        if (stickyBtn) stickyBtn.disabled = !available;
      }
    });
  }

  /* ---------- Barre d'achat mobile ---------- */
  const atcWrap = $('[data-elv-atc-wrap]');
  if (sticky && atcWrap && 'IntersectionObserver' in window) {
    const mq = window.matchMedia('(max-width: 989px)');
    let pastButton = false;
    let nearEnd = false;

    const render = () => {
      const visible = mq.matches && pastButton && !nearEnd;
      sticky.toggleAttribute('data-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      if (stickyBtn) stickyBtn.tabIndex = visible ? 0 : -1;
      document.body.classList.toggle('elv-has-sticky', visible);
    };

    new IntersectionObserver(([entry]) => {
      pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      render();
    }).observe(atcWrap);

    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (footer) {
      new IntersectionObserver(([entry]) => {
        nearEnd = entry.isIntersecting;
        render();
      }).observe(footer);
    }
    mq.addEventListener('change', render);
  }

  /* ---------- Lien vers les avis ---------- */
  $('[data-elv-rating-link]')?.addEventListener('click', (event) => {
    const target = document.getElementById('avis-clients');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  /* ---------- Mini-description ---------- */
  const mini = $('[data-elv-mini]');
  if (mini) {
    const text = mini.querySelector('[data-elv-mini-text]');
    const more = mini.querySelector('[data-elv-mini-more]');
    const check = () => {
      if (mini.hasAttribute('data-open')) return;
      more.hidden = text.scrollHeight <= text.clientHeight + 2;
    };
    check();
    window.addEventListener('resize', check, { passive: true });
    more.addEventListener('click', () => {
      const open = !mini.hasAttribute('data-open');
      mini.toggleAttribute('data-open', open);
      more.setAttribute('aria-expanded', String(open));
      more.textContent = open ? 'Réduire' : 'En savoir plus';
    });
  }

  /* ---------- Livraison estimée ---------- */
  const delivery = $('[data-elv-delivery]');
  if (delivery) {
    const min = parseInt(delivery.dataset.min, 10);
    const max = parseInt(delivery.dataset.max, 10);
    const businessDays = delivery.dataset.skipWeekends === 'true';
    if (!Number.isNaN(min) && !Number.isNaN(max) && max >= min) {
      const addDays = (count) => {
        const date = new Date();
        let added = 0;
        while (added < count) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (!businessDays || (day !== 0 && day !== 6)) added += 1;
        }
        return date;
      };
      const from = addDays(min);
      const to = addDays(max);
      const dayMonth = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' });
      const dayOnly = new Intl.DateTimeFormat('fr-FR', { day: 'numeric' });
      const sameMonth = from.getMonth() === to.getMonth();
      const text =
        min === max
          ? `Livraison estimée le ${dayMonth.format(from)}`
          : `Entre le ${sameMonth ? dayOnly.format(from) : dayMonth.format(from)} et le ${dayMonth.format(to)}`;
      delivery.querySelector('[data-elv-delivery-text]').textContent = text;
    }
  }
}

/* ------------------------------------------------------------------ */
/* Accordéons                                                         */
/* ------------------------------------------------------------------ */
function initAccordions(root) {
  root.querySelectorAll('[data-elv-acc]').forEach((trigger) => {
    if (!once(trigger, 'elvReady')) return;
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    if (trigger.getAttribute('aria-expanded') === 'true') panel.setAttribute('data-open', '');

    trigger.addEventListener('click', () => {
      const open = trigger.getAttribute('aria-expanded') !== 'true';
      trigger.setAttribute('aria-expanded', String(open));
      if (open) {
        panel.hidden = false;
        requestAnimationFrame(() => panel.setAttribute('data-open', ''));
      } else {
        panel.removeAttribute('data-open');
        const done = () => {
          if (trigger.getAttribute('aria-expanded') === 'false') panel.hidden = true;
        };
        panel.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 350);
      }
    });
  });
}

/* ------------------------------------------------------------------ */
/* Avis : « Voir plus »                                               */
/* ------------------------------------------------------------------ */
function initReviews(root) {
  const more = root.querySelector('[data-elv-rev-more]');
  if (!more || !once(more, 'elvReady')) return;
  more.addEventListener('click', () => {
    const hidden = [...root.querySelectorAll('[data-elv-rev-extra][hidden]')];
    hidden.slice(0, 6).forEach((el) => (el.hidden = false));
    if (hidden.length <= 6) more.remove();
  });
}

/* ------------------------------------------------------------------ */
/* Carrousel (recommandations)                                        */
/* ------------------------------------------------------------------ */
function initRail(root) {
  const rail = root.querySelector('[data-elv-rail]');
  if (!rail || !once(rail, 'elvReady')) return;
  const prev = root.querySelector('[data-elv-rail-prev]');
  const next = root.querySelector('[data-elv-rail-next]');
  const nav = root.querySelector('[data-elv-rail-nav]');

  const update = () => {
    const max = rail.scrollWidth - rail.clientWidth;
    if (nav) nav.style.visibility = max <= 4 ? 'hidden' : '';
    if (prev) prev.disabled = rail.scrollLeft <= 4;
    if (next) next.disabled = rail.scrollLeft >= max - 4;
  };
  const step = () => {
    const card = rail.firstElementChild;
    if (!card) return rail.clientWidth;
    const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    const unit = card.getBoundingClientRect().width + gap;
    return unit * Math.max(1, Math.floor(rail.clientWidth / unit));
  };
  prev?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
  next?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
  rail.addEventListener('scroll', update, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(update).observe(rail);
  update();
}

async function initRecommendations(root) {
  const box = root.querySelector('[data-elv-reco]');
  if (!box) return;
  if (!box.dataset.url) {
    initRail(box);
    return;
  }
  if (!once(box, 'elvReady')) return;

  const load = async () => {
    try {
      const html = await fetch(box.dataset.url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-elv-reco]');
      if (fresh && fresh.querySelector('.elv-card')) {
        box.innerHTML = fresh.innerHTML;
        initRail(box);
      }
    } catch (error) {
      /* Les recommandations restent masquées en cas d'échec. */
    }
  };

  // Chargées une fois la page affichée, pour ne jamais ralentir le premier écran.
  if (document.readyState === 'complete') {
    load();
  } else {
    window.addEventListener('load', load, { once: true });
  }
}

/* ------------------------------------------------------------------ */
function initAll(scope = document) {
  scope.querySelectorAll('[data-elv-product]').forEach(initProduct);
  initAccordions(scope);
  scope.querySelectorAll('[data-elv-reviews]').forEach(initReviews);
  if (scope.querySelector('[data-elv-reco]')) initRecommendations(scope);
}

initAll();

document.addEventListener('shopify:section:load', (event) => initAll(event.target));
